import { Action } from 'redux';
import { Dispatch } from 'redux';
import { Venues, VendorsApi } from '@weddingspot/ws-api-client';

import {
    NEXT_VENUE_IMAGES_SLIDE,
    PREV_VENUE_IMAGES_SLIDE,
    LOAD_VENUE_DETAILS,
    LOAD_UNLISTED_VENUE,
    LOAD_PERSONALIZED_VENUE_DETAILS,
    SHOW_PHONE_NUMBER,
    SET_ACTIVE_TAB,
    OPEN_MORE_TAB,
    CLOSE_MORE_TAB,
    SET_VENUE_AS_FAVORITE,
    UNSET_VENUE_FROM_FAVORITE,
    VENUE_PROFILE_RECOMMENDATIONS_LOADED,
    VENUE_PROFILE_RECOMMENDATIONS_FAILED,
    VENUE_PROFILE_SHOW_INQUIRY_MODAL,
    VENUE_PROFILE_HIDE_INQUIRY_MODAL,
    VENUE_PROFILE_RENDER_RDV,
} from '../constants';

export interface LoadVenueDetails extends Action {
    type: LOAD_VENUE_DETAILS;
    payload: Venues.VenueDetails;
}

export interface LoadUnlistedVenue extends Action {
    type: LOAD_UNLISTED_VENUE;
    payload: Venues.UnlistedVenueDetails;
}

export interface LoadPersonalizedVenueDetails extends Action {
    type: LOAD_PERSONALIZED_VENUE_DETAILS;
    payload: Venues.VenueDetailsPersonalized;
}

export interface SetNextVenueImageSlide {
    type: NEXT_VENUE_IMAGES_SLIDE;
}

export interface SetPrevVenueImageSlide {
    type: PREV_VENUE_IMAGES_SLIDE;
}

export interface ShowPhoneNumber {
    type: SHOW_PHONE_NUMBER;
}

export interface OpenMoreTab {
    type: OPEN_MORE_TAB;
}

export interface CloseMoreTab {
    type: CLOSE_MORE_TAB;
}

export interface SetActiveTab {
    type: SET_ACTIVE_TAB;
    payload: number;
}

export interface SetVenueFavorite {
    type: SET_VENUE_AS_FAVORITE;
}

export interface UnsetVenueFavorite {
    type: UNSET_VENUE_FROM_FAVORITE;
}

export interface RecommendationsLoaded {
    type: VENUE_PROFILE_RECOMMENDATIONS_LOADED;
    payload: Venues.VenueRecommendation[];
}

export interface RecommendationsFailed {
    type: VENUE_PROFILE_RECOMMENDATIONS_FAILED;
}

export interface ShowInquiryModal {
    type: VENUE_PROFILE_SHOW_INQUIRY_MODAL;
}

export interface HideInquiryModal {
    type: VENUE_PROFILE_HIDE_INQUIRY_MODAL;
}

export interface RenderRdvProfile {
    type: VENUE_PROFILE_RENDER_RDV;
}

export type VenueProfileAction =
    | LoadPersonalizedVenueDetails
    | SetVenueFavorite
    | UnsetVenueFavorite
    | LoadVenueDetails
    | SetNextVenueImageSlide
    | SetPrevVenueImageSlide
    | ShowPhoneNumber
    | OpenMoreTab
    | CloseMoreTab
    | SetActiveTab
    | RecommendationsLoaded
    | RecommendationsFailed
    | ShowInquiryModal
    | HideInquiryModal
    | RenderRdvProfile
    | LoadUnlistedVenue;

export function setVenueFavorite(): (dispatch: Dispatch<SetVenueFavorite>) => void {
    return function (dispatch: Dispatch<SetVenueFavorite>) {
        dispatch({
            type: SET_VENUE_AS_FAVORITE,
        });
    };
}

export function unsetVenueFavorite(): (dispatch: Dispatch<UnsetVenueFavorite>) => void {
    return function (dispatch: Dispatch<UnsetVenueFavorite>) {
        dispatch({
            type: UNSET_VENUE_FROM_FAVORITE,
        });
    };
}

export function loadVenueDetails(venueDetails: Venues.VenueDetails): (dispatch: Dispatch<LoadVenueDetails>) => void {
    return function (dispatch: Dispatch<LoadVenueDetails>) {
        dispatch({
            type: LOAD_VENUE_DETAILS,
            payload: venueDetails,
        });
    };
}

export function loadUnlistedVenue(venueDetails: Venues.UnlistedVenueDetails): (dispatch: Dispatch<LoadUnlistedVenue>) => void {
    return function (dispatch: Dispatch<LoadUnlistedVenue>) {
        dispatch({
            type: LOAD_UNLISTED_VENUE,
            payload: venueDetails,
        });
    };
}

export function loadPersonalizedVenueDetails(
    venueDetails: Venues.VenueDetailsPersonalized
): (dispatch: Dispatch<LoadPersonalizedVenueDetails>) => void {
    return function (dispatch: Dispatch<LoadPersonalizedVenueDetails>) {
        dispatch({
            type: LOAD_PERSONALIZED_VENUE_DETAILS,
            payload: venueDetails,
        });
    };
}

export function slideToNextImage(): (dispatch: Dispatch<SetNextVenueImageSlide>) => void {
    return function (dispatch: Dispatch<SetNextVenueImageSlide>) {
        dispatch({
            type: NEXT_VENUE_IMAGES_SLIDE,
        });
    };
}

export function slideToPrevImage(): (dispatch: Dispatch<SetPrevVenueImageSlide>) => void {
    return function (dispatch: Dispatch<SetPrevVenueImageSlide>) {
        dispatch({
            type: PREV_VENUE_IMAGES_SLIDE,
        });
    };
}

export function showPhoneNumber(): (dispatch: Dispatch<ShowPhoneNumber>) => void {
    return function (dispatch: Dispatch<ShowPhoneNumber>) {
        dispatch({
            type: SHOW_PHONE_NUMBER,
        });
    };
}

export function setActiveTab(tabIndex: number): (dispatch: Dispatch<SetActiveTab>) => void {
    return function (dispatch: Dispatch<SetActiveTab>) {
        dispatch({
            type: SET_ACTIVE_TAB,
            payload: tabIndex,
        });
    };
}

export function openMoreTab(): (dispatch: Dispatch<OpenMoreTab>) => void {
    return function (dispatch: Dispatch<OpenMoreTab>) {
        dispatch({
            type: OPEN_MORE_TAB,
        });
    };
}

export function closeMoreTab(): (dispatch: Dispatch<CloseMoreTab>) => void {
    return function (dispatch: Dispatch<CloseMoreTab>) {
        dispatch({
            type: CLOSE_MORE_TAB,
        });
    };
}

export function tryLoadRecommendations(
    venueId: number,
    isRdv: boolean,
    excludeIds?: number[]
): (dispatch: Dispatch<VenueProfileAction>) => Promise<void> {
    return function (dispatch: Dispatch<VenueProfileAction>) {
        let params: Venues.VenueRecommendationsQueryParams = {
            seed_ids: [venueId],
            count: 3,
        };
        if (isRdv) {
            params.rdvs = 1;
        }
        if (excludeIds) {
            params.exclude_ids = excludeIds;
        }
        return VendorsApi.getVenueRecommendations(params)
            .then((resp) => {
                dispatch({
                    type: VENUE_PROFILE_RECOMMENDATIONS_LOADED,
                    payload: resp,
                });
            })
            .catch((_) => {
                dispatch({
                    type: VENUE_PROFILE_RECOMMENDATIONS_FAILED,
                });
            });
    };
}

export function showInquiryModal() {
    return (dispatch: Dispatch<ShowInquiryModal>) => {
        dispatch({ type: VENUE_PROFILE_SHOW_INQUIRY_MODAL });
    };
}

export function hideInquiryModal() {
    return (dispatch: Dispatch<HideInquiryModal>) => {
        dispatch({ type: VENUE_PROFILE_HIDE_INQUIRY_MODAL });
    };
}

export function renderRdvProfile() {
    return (dispatch: Dispatch<RenderRdvProfile>) => {
        dispatch({ type: VENUE_PROFILE_RENDER_RDV });
    };
}
